<template>
  <div class="price-histogram-filter">
    <div v-if="showHistogram" class="histogram-panel">
      <p class="histogram-hint">{{ $l('priceHistogramHint') }}</p>

      <div
        ref="histogramWrap"
        class="histogram-wrap"
        dir="ltr"
        @mouseleave="hoveredBar = null"
      >
        <svg
          class="histogram-svg"
          :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <line
            :x1="0"
            :y1="chartHeight - 1"
            :x2="chartWidth"
            :y2="chartHeight - 1"
            class="histogram-baseline"
          />

          <rect
            v-for="bar in bars"
            :key="bar.index"
            :x="bar.x"
            :y="bar.y"
            :width="bar.width"
            :height="bar.height"
            :rx="Math.min(bar.width * 0.22, 3)"
            :class="[
              'histogram-bar',
              'histogram-bar--clickable',
              bar.count > 0 ? 'histogram-bar--has-products' : 'histogram-bar--empty',
              hoveredBar && hoveredBar.index === bar.index ? 'histogram-bar--hovered' : '',
            ]"
            :fill="bar.fill"
            @mouseenter="onBarHover(bar)"
            @mousemove="onBarHover(bar)"
            @click="onBarClick(bar)"
          />
        </svg>

        <transition name="tooltip-fade">
          <div
            v-if="hoveredBar"
            class="histogram-tooltip"
            :style="tooltipStyle"
          >
            <span class="tooltip-price">{{ formatBarRange(hoveredBar) }}</span>
            <span v-if="hoveredBar.count > 0" class="tooltip-count">{{ formatProductCount(hoveredBar.count) }}</span>
          </div>
        </transition>
      </div>

      <div class="histogram-axis" dir="ltr">
        <span class="histogram-axis__label">{{ formatAxisPrice(boundsMin) }}</span>
        <span class="histogram-axis__hint">{{ $l('priceLowToHigh') }}</span>
        <span class="histogram-axis__label">{{ formatAxisPrice(boundsMax) }}</span>
      </div>
    </div>

    <div class="filter-panel" :class="{ 'filter-panel--solo': !showHistogram }" dir="ltr">
      <p class="filter-label">{{ $l('filterByPrice') }}</p>

      <div class="price-inputs">
        <label class="price-input-group">
          <span class="price-input-label">{{ $l('minPrice') }}</span>
          <input
            class="price-input"
            type="number"
            :value="minPrice != null ? minPrice : ''"
            :placeholder="String(boundsMin)"
            min="0"
            @input="onMinInput($event.target.value)"
          >
        </label>
        <label class="price-input-group">
          <span class="price-input-label">{{ $l('maxPrice') }}</span>
          <input
            class="price-input"
            type="number"
            :value="maxPrice != null ? maxPrice : ''"
            :placeholder="String(boundsMax)"
            min="0"
            @input="onMaxInput($event.target.value)"
          >
        </label>
      </div>

      <div ref="priceSlider" class="price-slider"></div>
    </div>
  </div>
</template>

<script>
import noUiSlider from 'nouislider';
import 'nouislider/distribute/nouislider.css';
import {
  buildHistogramBuckets,
  extractPricedProducts,
  getHistogramPriceBounds,
  getPriceBucketColor,
} from '@/utils/priceHistogram';
import { getRelevantProductsForHistogram } from '@/utils/productFilters';

const CHART_WIDTH = 240;
const CHART_HEIGHT = 80;
const BAR_GAP = 3;

export default {
  name: 'PriceHistogramFilter',
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    priceBounds: {
      type: Object,
      default: () => ({ min: 0, max: 1000 }),
    },
    minPrice: {
      type: Number,
      default: null,
    },
    maxPrice: {
      type: Number,
      default: null,
    },
    currencySign: {
      type: String,
      default: '$',
    },
    showHistogram: {
      type: Boolean,
      default: true,
    },
    hideAccessories: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      chartWidth: CHART_WIDTH,
      chartHeight: CHART_HEIGHT,
      sliderReady: false,
      suppressSliderEmit: false,
      hoveredBar: null,
      tooltipLeft: 0,
    };
  },
  computed: {
    histogramProducts() {
      return getRelevantProductsForHistogram(
        this.products,
        this.hideAccessories,
        this.searchQuery
      );
    },
    histogramBounds() {
      return getHistogramPriceBounds(this.histogramProducts);
    },
    boundsMin() {
      return this.histogramBounds.min || 0;
    },
    boundsMax() {
      return Math.max(this.histogramBounds.max || 1000, this.boundsMin + 1);
    },
    sliderMin() {
      return this.minPrice != null ? this.minPrice : this.boundsMin;
    },
    sliderMax() {
      return this.maxPrice != null ? this.maxPrice : this.boundsMax;
    },
    histogram() {
      const pricedProducts = extractPricedProducts(this.histogramProducts);
      return buildHistogramBuckets(pricedProducts, {
        min: this.boundsMin,
        max: this.boundsMax,
      });
    },
    bars() {
      const { buckets, bucketCount } = this.histogram;
      const barWidth = (CHART_WIDTH - BAR_GAP * (bucketCount - 1)) / bucketCount;
      const maxCount = Math.max(...buckets.map((bucket) => bucket.count), 1);

      return buckets.map((bucket) => {
        const height = bucket.count > 0
          ? Math.max((bucket.count / maxCount) * (CHART_HEIGHT - 12), 8)
          : 10;
        const priceRatio = bucketCount > 1
          ? bucket.index / (bucketCount - 1)
          : 0;
        const isHovered = this.hoveredBar && this.hoveredBar.index === bucket.index;

        return {
          ...bucket,
          x: bucket.index * (barWidth + BAR_GAP),
          y: CHART_HEIGHT - height - 2,
          width: barWidth,
          height,
          centerX: bucket.index * (barWidth + BAR_GAP) + barWidth / 2,
          fill: getPriceBucketColor(priceRatio, {
            hover: isHovered,
            empty: bucket.count === 0,
            density: bucket.densityRatio,
          }),
        };
      });
    },
    tooltipStyle() {
      return {
        left: `${this.tooltipLeft}px`,
      };
    },
  },
  watch: {
    products: {
      deep: true,
      handler() {
        this.refreshSliderRange();
      },
    },
    histogramBounds: {
      deep: true,
      handler() {
        this.refreshSliderRange();
      },
    },
    priceBounds: {
      deep: true,
      handler() {
        this.createSlider();
      },
    },
    minPrice() {
      this.syncSlider();
    },
    maxPrice() {
      this.syncSlider();
    },
  },
  mounted() {
    this.createSlider();
  },
  beforeDestroy() {
    this.destroySlider();
  },
  methods: {
    formatBarRange(bar) {
      const min = Math.round(bar.min);
      const max = Math.round(bar.max);
      if (min === max) {
        return `${this.currencySign}${min}`;
      }
      return `${this.currencySign}${min} – ${this.currencySign}${max}`;
    },
    formatAxisPrice(value) {
      return `${this.currencySign}${Math.round(value)}`;
    },
    formatProductCount(count) {
      const template = this.$l('productsInBracket');
      return template.replace('{count}', count);
    },
    onBarHover(bar) {
      this.hoveredBar = bar;

      const wrap = this.$refs.histogramWrap;
      if (!wrap) {
        return;
      }

      const wrapRect = wrap.getBoundingClientRect();
      const barCenterRatio = bar.centerX / CHART_WIDTH;
      const pixelLeft = barCenterRatio * wrapRect.width;
      this.tooltipLeft = Math.max(40, Math.min(pixelLeft, wrapRect.width - 40));
    },
    destroySlider() {
      if (this.$refs.priceSlider && this.$refs.priceSlider.noUiSlider) {
        this.$refs.priceSlider.noUiSlider.destroy();
      }
      this.sliderReady = false;
    },
    createSlider() {
      this.$nextTick(() => {
        if (!this.$refs.priceSlider) {
          return;
        }

        this.destroySlider();
        this.suppressSliderEmit = true;

        noUiSlider.create(this.$refs.priceSlider, {
          start: [this.sliderMin, this.sliderMax],
          connect: true,
          step: 1,
          range: {
            min: this.boundsMin,
            max: this.boundsMax,
          },
        });

        this.$refs.priceSlider.noUiSlider.on('slide', (values) => {
          if (this.suppressSliderEmit) {
            return;
          }
          const min = Math.round(Number(values[0]));
          const max = Math.round(Number(values[1]));
          this.$emit('update:minPrice', min);
          this.$emit('update:maxPrice', max);
        });

        this.sliderReady = true;
        this.$nextTick(() => {
          this.suppressSliderEmit = false;
        });
      });
    },
    refreshSliderRange() {
      if (!this.sliderReady || !this.$refs.priceSlider?.noUiSlider) {
        this.createSlider();
        return;
      }

      this.$refs.priceSlider.noUiSlider.updateOptions({
        range: {
          min: this.boundsMin,
          max: this.boundsMax,
        },
      });
      this.syncSlider();
    },
    syncSlider() {
      this.$nextTick(() => {
        if (!this.$refs.priceSlider?.noUiSlider) {
          return;
        }
        this.suppressSliderEmit = true;
        this.$refs.priceSlider.noUiSlider.set([this.sliderMin, this.sliderMax], false);
        this.$nextTick(() => {
          this.suppressSliderEmit = false;
        });
      });
    },
    onMinInput(value) {
      const min = value === '' ? null : Number(value);
      this.$emit('update:minPrice', min);
      this.syncSlider();
    },
    onMaxInput(value) {
      const max = value === '' ? null : Number(value);
      this.$emit('update:maxPrice', max);
      this.syncSlider();
    },
    onBarClick(bar) {
      if (navigator.vibrate) {
        navigator.vibrate(12);
      }

      this.$emit('bar-click', {
        min: bar.min,
        max: bar.max,
        firstProductKey: bar.firstProductKey,
      });
    },
  },
};
</script>

<style scoped>
.price-histogram-filter {
  width: 100%;
}

.histogram-panel {
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  padding: 12px 12px 10px;
}

.histogram-hint {
  margin: 0 0 10px;
  font-size: 11px;
  line-height: 1.4;
  color: #64748b;
}

.histogram-wrap {
  position: relative;
  width: 100%;
  direction: ltr;
}

.histogram-svg {
  display: block;
  width: 100%;
  height: 80px;
  direction: ltr;
}

.histogram-axis {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.35rem;
  padding: 0 2px;
  direction: ltr;
}

.histogram-axis__label {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
}

.histogram-axis__hint {
  font-size: 9px;
  color: #94a3b8;
  text-align: center;
  white-space: nowrap;
}

.histogram-baseline {
  stroke: #e2e8f0;
  stroke-width: 1;
}

.histogram-bar {
  transition: fill 0.15s ease, transform 0.12s ease;
}

.histogram-bar--clickable {
  cursor: pointer;
}

.histogram-bar--clickable:hover,
.histogram-bar--hovered {
  filter: brightness(1.08);
}

.histogram-bar--empty {
  stroke: rgba(148, 163, 184, 0.35);
  stroke-width: 0.5;
}

.histogram-tooltip {
  position: absolute;
  top: -6px;
  transform: translate(-50%, -100%);
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 10px;
  border-radius: 10px;
  background: #0f172a;
  color: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.22);
  pointer-events: none;
  white-space: nowrap;
}

.histogram-tooltip::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #0f172a;
}

.tooltip-price {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.tooltip-count {
  font-size: 10px;
  color: #94a3b8;
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-100% + 4px));
}

.filter-panel {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

.filter-panel--solo {
  margin-top: 0;
  padding-top: 0;
  border-top: 0;
}

.filter-label {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.price-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}

.price-input-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.price-input-label {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
}

.price-input {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 8px 10px;
  font-size: 13px;
  color: #0f172a;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.price-input::placeholder {
  color: #cbd5e1;
}

.price-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.price-slider {
  margin: 4px 8px 0;
  height: 28px;
}

.price-slider ::v-deep .noUi-target {
  background: #e2e8f0;
  border: 0;
  border-radius: 999px;
  box-shadow: none;
}

.price-slider ::v-deep .noUi-base {
  height: 4px;
}

.price-slider ::v-deep .noUi-connect {
  background: linear-gradient(90deg, #22c55e, #3b82f6, #ef4444);
}

.price-slider ::v-deep .noUi-handle {
  width: 18px;
  height: 18px;
  right: -9px;
  top: -8px;
  border-radius: 999px;
  border: 2px solid #2563eb;
  background: #fff;
  box-shadow: 0 2px 10px rgba(37, 99, 235, 0.28);
  cursor: grab;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.price-slider ::v-deep .noUi-handle:focus {
  outline: none;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.18);
}

.price-slider ::v-deep .noUi-handle::before,
.price-slider ::v-deep .noUi-handle::after {
  display: none;
}

@media (max-width: 1023px) {
  .price-input {
    min-height: 48px;
    padding: 12px 12px;
    font-size: 14px;
  }

  .price-slider ::v-deep .noUi-handle {
    width: 24px;
    height: 24px;
    right: -12px;
    top: -11px;
  }
}
</style>
